import { ModuleTypeEnum, ModuleColumnDefinition } from '../module';
import { FieldTypeEnum } from '../custom-field.types';
import { InventoryItem, InventoryCategory, InventoryLocation, InventoryMovement } from '../inventory';

/**
 * Standard width categories for consistent column sizing across all inventory tables
 */
const INVENTORY_COLUMN_WIDTHS = {
  // Very narrow - icons, small numbers, boolean indicators
  VERY_NARROW: { width: "50px", minWidth: "35px" },
  // Narrow - short text, priorities, small dropdowns
  NARROW: { width: "120px", minWidth: "80px" },
  // Medium - standard text fields, dates, status fields
  MEDIUM: { width: "160px", minWidth: "120px" },
  // Wide - longer text fields, descriptions, addresses
  WIDE: { width: "200px", minWidth: "140px" },
  // Very wide - names, main content
  VERY_WIDE: { width: "280px", minWidth: "200px" },
} as const;

/**
 * Standard column definitions for the Inventory Item module
 */
export const INVENTORY_ITEM_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'name',
    name: 'name' as keyof InventoryItem,
    label: 'Item Name',
    description: 'Name of the inventory item',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.VERY_WIDE,
  },
  {
    id: 'sku',
    name: 'sku' as keyof InventoryItem,
    label: 'SKU',
    description: 'Stock keeping unit identifier',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'itemType',
    name: 'itemType' as keyof InventoryItem,
    label: 'Type',
    description: 'Type of inventory item',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    options: [
      { value: 'INVENTORY', label: 'Inventory', color: '#3b82f6' },
      { value: 'SERVICE', label: 'Service', color: '#8b5cf6' },
      { value: 'NON_INVENTORY', label: 'Non-inventory', color: '#6b7280' },
    ],
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'categoryId',
    name: 'categoryId' as keyof InventoryItem,
    label: 'Category',
    description: 'Category of the item',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'status',
    name: 'status' as keyof InventoryItem,
    label: 'Status',
    description: 'Current stock status',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    options: [
      { value: 'IN_STOCK', label: 'In Stock', color: '#1ead5b' },
      { value: 'OUT_OF_STOCK', label: 'Out of Stock', color: '#ef4444' },
      { value: 'RESERVED', label: 'Reserved', color: '#f97316' },
      { value: 'DAMAGED', label: 'Damaged', color: '#dc2626' },
      { value: 'DISCONTINUED', label: 'Discontinued', color: '#6b7280' },
    ],
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'quantity',
    name: 'quantity' as keyof InventoryItem,
    label: 'Qty',
    description: 'Current quantity in stock',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'unitPrice',
    name: 'unitPrice' as keyof InventoryItem,
    label: 'Price',
    description: 'Unit selling price',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'purchaseCost',
    name: 'purchaseCost' as keyof InventoryItem,
    label: 'Cost',
    description: 'Purchase cost per unit',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'defaultLocationId',
    name: 'defaultLocationId' as keyof InventoryItem,
    label: 'Location',
    description: 'Default storage location',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'preferredSupplier',
    name: 'preferredSupplier' as keyof InventoryItem,
    label: 'Supplier',
    description: 'Preferred supplier for this item',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'isActive',
    name: 'isActive' as keyof InventoryItem,
    label: 'Active',
    description: 'Whether the item is active',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof InventoryItem,
    label: 'Created',
    description: 'Date when item was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 11,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
];

/**
 * Standard column definitions for the Inventory Category module
 */
export const INVENTORY_CATEGORY_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'name',
    name: 'name' as keyof InventoryCategory,
    label: 'Category Name',
    description: 'Name of the category',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.VERY_WIDE,
  },
  {
    id: 'code',
    name: 'code' as keyof InventoryCategory,
    label: 'Code',
    description: 'Category code',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'type',
    name: 'type' as keyof InventoryCategory,
    label: 'Type',
    description: 'Category type',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    options: [
      { value: 'PRODUCT', label: 'Product', color: '#3b82f6' },
      { value: 'MATERIAL', label: 'Material', color: '#8b5cf6' },
      { value: 'EQUIPMENT', label: 'Equipment', color: '#f97316' },
      { value: 'SERVICE', label: 'Service', color: '#10b981' },
    ],
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'itemCount',
    name: 'itemCount' as keyof InventoryCategory,
    label: 'Items',
    description: 'Number of items in this category',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'description',
    name: 'description' as keyof InventoryCategory,
    label: 'Description',
    description: 'Category description',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.TEXT,
    ...INVENTORY_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'isActive',
    name: 'isActive' as keyof InventoryCategory,
    label: 'Active',
    description: 'Whether the category is active',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof InventoryCategory,
    label: 'Created',
    description: 'Date when category was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
];

/**
 * Standard column definitions for the Inventory Location module
 */
export const INVENTORY_LOCATION_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'name',
    name: 'name' as keyof InventoryLocation,
    label: 'Location Name',
    description: 'Name of the location',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.VERY_WIDE,
  },
  {
    id: 'code',
    name: 'code' as keyof InventoryLocation,
    label: 'Code',
    description: 'Location code',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'type',
    name: 'type' as keyof InventoryLocation,
    label: 'Type',
    description: 'Location type',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    options: [
      { value: 'WAREHOUSE', label: 'Warehouse', color: '#3b82f6' },
      { value: 'STORE', label: 'Store', color: '#8b5cf6' },
      { value: 'OFFICE', label: 'Office', color: '#f97316' },
      { value: 'VEHICLE', label: 'Vehicle', color: '#10b981' },
    ],
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'itemCount',
    name: 'itemCount' as keyof InventoryLocation,
    label: 'Items',
    description: 'Number of items at this location',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'address',
    name: 'address' as keyof InventoryLocation,
    label: 'Address',
    description: 'Physical address of the location',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.TEXT,
    ...INVENTORY_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'description',
    name: 'description' as keyof InventoryLocation,
    label: 'Description',
    description: 'Location description',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.TEXT,
    ...INVENTORY_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'isActive',
    name: 'isActive' as keyof InventoryLocation,
    label: 'Active',
    description: 'Whether the location is active',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof InventoryLocation,
    label: 'Created',
    description: 'Date when location was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
];

/**
 * Standard column definitions for the Inventory Movement module
 */
export const INVENTORY_MOVEMENT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'itemId',
    name: 'itemId' as keyof InventoryMovement,
    label: 'Item',
    description: 'Item that was moved',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...INVENTORY_COLUMN_WIDTHS.VERY_WIDE,
  },
  // NOTE: Movements are an immutable audit ledger — type, reason, quantity,
  // locations, dates, and the actor are sealed once the movement is recorded
  // so the stock history can always be reconstructed. The only editable
  // column is `notes` (free-text commentary, not a ledger fact). To correct
  // a mistake, record a reversing Adjustment instead.
  {
    id: 'type',
    name: 'type' as keyof InventoryMovement,
    label: 'Type',
    description: 'Movement type',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 1,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    options: [
      { value: 'IN', label: 'Stock In', color: '#10b981' },
      { value: 'OUT', label: 'Stock Out', color: '#ef4444' },
      { value: 'TRANSFER', label: 'Transfer', color: '#3b82f6' },
      { value: 'ADJUSTMENT', label: 'Adjustment', color: '#f97316' },
    ],
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'reason',
    name: 'reason' as keyof InventoryMovement,
    label: 'Reason',
    description: 'Reason for the movement',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    options: [
      { value: 'PURCHASE', label: 'Purchase', color: '#10b981' },
      { value: 'SALE', label: 'Sale', color: '#3b82f6' },
      { value: 'RETURN', label: 'Return', color: '#8b5cf6' },
      { value: 'DAMAGE', label: 'Damage', color: '#ef4444' },
      { value: 'THEFT', label: 'Theft/Loss', color: '#dc2626' },
      { value: 'TRANSFER', label: 'Transfer', color: '#f97316' },
      { value: 'CYCLE_COUNT', label: 'Cycle Count', color: '#6b7280' },
      { value: 'CORRECTION', label: 'Correction', color: '#64748b' },
    ],
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'quantity',
    name: 'quantity' as keyof InventoryMovement,
    label: 'Qty',
    description: 'Quantity moved',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 3,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...INVENTORY_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'fromLocationId',
    name: 'fromLocationId' as keyof InventoryMovement,
    label: 'From',
    description: 'Source location',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 4,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'toLocationId',
    name: 'toLocationId' as keyof InventoryMovement,
    label: 'To',
    description: 'Destination location',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 5,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'user',
    name: 'user' as keyof InventoryMovement,
    label: 'User',
    description: 'User who performed the movement',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 6,
    fieldType: FieldTypeEnum.enum.USER,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'movementDate',
    name: 'movementDate' as keyof InventoryMovement,
    label: 'Date',
    description: 'Date of the movement',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    isReadOnly: true,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'notes',
    name: 'notes' as keyof InventoryMovement,
    label: 'Notes',
    description: 'Additional notes',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.TEXT,
    ...INVENTORY_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof InventoryMovement,
    label: 'Created',
    description: 'Date when movement was recorded',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...INVENTORY_COLUMN_WIDTHS.MEDIUM,
  },
];

/**
 * Get module type for inventory item columns
 */
export function getInventoryItemModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.INVENTORY;
}

/**
 * Get module type for inventory category columns
 */
export function getInventoryCategoryModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.INVENTORY;
}

/**
 * Get module type for inventory location columns
 */
export function getInventoryLocationModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.INVENTORY;
}

/**
 * Get module type for inventory movement columns
 */
export function getInventoryMovementModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.INVENTORY;
}
